import { useCallback, useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { TableColumnDefinition } from '@antimatterhq/ui/types/TableColumnDefinition';
import { tableColumnDefinitionsWithSelection } from '@antimatterhq/ui/utils/table/helpers';
import { useAtomCallback } from 'jotai/utils';
import isEqual from 'react-fast-compare';
import {
  queryTabAtomFamily,
  updateTabAtomFamily,
} from '@/atoms/query/queryAtoms';
import { useGetQueryLookup } from '@/hooks/dbui/useGetQueryLookup';

export function useQueryResultsColumnDefinitions(
  tabId: string,
): TableColumnDefinition[] {
  const tab = useAtomValue(queryTabAtomFamily(tabId));
  const updateTab = useSetAtom(updateTabAtomFamily(tabId));

  const request = useMemo(
    () => ({
      rowCount: 1,
      refinements: tab?.refinements || [],
    }),
    [tab?.refinements],
  );

  // Use the existing query lookup hook to fetch column definitions
  const { data } = useGetQueryLookup(
    tab?.queryId,
    request,
    !tab?.queryId ||
      tab?.status !== 'success' ||
      !!tab?.columnDefinitions?.length,
  );

  // Transform the data into column definitions
  const transformColumns = useAtomCallback(
    useCallback(
      (get, set) => {
        if (!data) return;

        // TODO: This is a hack to handle the case where the query returns no results.
        // We should instead show a message to the user that the query returned no results.
        if (!data?.data?.length && !data?.columns?.length) {
          set(updateTabAtomFamily(tabId), { status: 'idle' });

          console.warn(
            'Query returned no results. Try updating the query or the time range.',
          );

          return;
        }

        const columns: TableColumnDefinition[] =
          data?.columns
            ?.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            ?.filter((col) => col.name !== '_row_number' && col.name !== 'id')
            ?.filter((col) => col.name !== null && col.name !== undefined)
            .map((col) => {
              const { name } = col;

              return {
                id: name ?? '',
                title: name ?? '',
                width: 150,
                enableSorting: false,
                enableFiltering: false,
                enableResize: true,
              };
            }) ?? ([] as TableColumnDefinition[]);

        columns.push({
          id: 'edit',
          tooltip: false,
          title: '',
          width: 40,
          enableResize: false,
          enableFiltering: false,
          enableSorting: false,
          pinned: 'right',
        });

        const transformedColumns = tableColumnDefinitionsWithSelection(columns);

        const currentColumnDefinitions = get(queryTabAtomFamily(tabId));

        if (
          !isEqual(
            currentColumnDefinitions?.columnDefinitions,
            transformedColumns,
          )
        ) {
          set(updateTabAtomFamily(tabId), {
            columnDefinitions: transformedColumns,
          });
        }
      },
      [data, tabId],
    ),
  );

  useEffect(() => {
    transformColumns();
  }, [transformColumns, updateTab, tab?.columnDefinitions?.length]);

  return tab?.columnDefinitions ?? [];
}
